import { domainError, type DomainError } from '../shared/domain-error';
import { compareNames, normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type { DisciplineId, ProjectId } from '../shared/ids';
import { STRUCTURE_ERROR, STRUCTURE_ERROR_MESSAGE } from './errors';

/**
 * Disciplina de engenharia.
 *
 * É **catálogo**, não atributo do pavimento (RN-007). `projectId` ausente = disciplina
 * global do sistema, disponível para todas as obras; preenchido = disciplina específica
 * daquela obra.
 *
 * A disciplina não duplica o pavimento: ela se relaciona a ele através de `Activity`.
 */
export interface Discipline {
  readonly id: DisciplineId;
  /** Ausente = disciplina global do sistema. */
  readonly projectId?: ProjectId;
  readonly name: string;
  readonly code?: string;
  /** Cor de apoio na árvore. Nunca é o único indicador de nada. */
  readonly color?: string;
  readonly position: number;
  readonly isActive: boolean;
  readonly deletedAt?: string;
}

export interface CreateDisciplineInput {
  readonly id: DisciplineId;
  readonly projectId?: ProjectId;
  readonly name: string;
  readonly code?: string;
  readonly color?: string;
  readonly position: number;
}

export function createDiscipline(input: CreateDisciplineInput): Result<Discipline, DomainError> {
  const name = input.name.trim();
  if (name.length === 0) {
    return err(
      domainError(STRUCTURE_ERROR.DISCIPLINE_NOT_FOUND, 'O nome da disciplina é obrigatório.'),
    );
  }
  return ok({
    id: input.id,
    name,
    position: input.position,
    isActive: true,
    ...(input.projectId === undefined ? {} : { projectId: input.projectId }),
    ...(input.code === undefined ? {} : { code: input.code }),
    ...(input.color === undefined ? {} : { color: input.color }),
  });
}

/**
 * Campos editáveis de uma disciplina já cadastrada.
 *
 * A identidade é o **nome** — trocá-lo reescreve a chave natural, e por isso o caso de
 * uso reverifica a unicidade antes de gravar. `undefined` remove o campo opcional: quem
 * apaga o código na tela quer a disciplina sem código, não o código anterior de volta.
 */
export interface EditDisciplineInput {
  readonly name: string;
  readonly code?: string;
  readonly color?: string;
}

export function editDiscipline(
  discipline: Discipline,
  input: EditDisciplineInput,
): Result<Discipline, DomainError> {
  const name = input.name.trim();
  if (name.length === 0) {
    return err(
      domainError(STRUCTURE_ERROR.DISCIPLINE_NOT_FOUND, 'O nome da disciplina é obrigatório.'),
    );
  }

  const { code: _code, color: _color, ...rest } = discipline;

  return ok({
    ...rest,
    name,
    ...(input.code === undefined ? {} : { code: input.code }),
    ...(input.color === undefined ? {} : { color: input.color }),
  });
}

/**
 * Liga e desliga a disciplina no catálogo.
 *
 * Desligar não é excluir: as frentes já cadastradas continuam existindo, e o que já foi
 * planejado e conferido nelas fica intacto. O que a disciplina inativa deixa de fazer é
 * aparecer como opção para novas frentes **e receber planejamento novo** — a mesma regra
 * do serviço desativado (RN-230). É o caminho para aposentar "Impermeabilização" sem
 * apagar o histórico das obras que a usaram.
 */
export function setDisciplineActive(discipline: Discipline, isActive: boolean): Discipline {
  return { ...discipline, isActive };
}

/**
 * Cores oferecidas na tela e usadas para colorir o que entra pela importação.
 *
 * São de apoio na árvore, nunca o único indicador de nada — o nome está sempre escrito
 * ao lado.
 */
export const DISCIPLINE_PALETTE: readonly string[] = [
  '#8b6f47',
  '#6b7280',
  '#c2703d',
  '#d4a017',
  '#3b82c4',
  '#2f855a',
  '#9b4dca',
  '#c53030',
];

/** Cor estável para uma disciplina que entrou sem cor — a posição decide, não o acaso. */
export function paletteColorAt(position: number): string {
  return DISCIPLINE_PALETTE[Math.abs(position) % DISCIPLINE_PALETTE.length]!;
}

const GLOBAL_SCOPE = '*';

/** Chave natural: única no escopo global ou dentro da obra (RN-007). */
export function disciplineNaturalKey(projectId: ProjectId | undefined, name: string): string {
  return `${projectId ?? GLOBAL_SCOPE}|${normalizeName(name)}`;
}

export function disciplineNameTakenError(name: string): DomainError {
  return domainError(
    STRUCTURE_ERROR.DISCIPLINE_NAME_TAKEN,
    STRUCTURE_ERROR_MESSAGE[STRUCTURE_ERROR.DISCIPLINE_NAME_TAKEN]!,
    { name },
  );
}

/** A disciplina está disponível para esta obra? Globais estão disponíveis para todas. */
export function isAvailableForProject(discipline: Discipline, projectId: ProjectId): boolean {
  return discipline.projectId === undefined || discipline.projectId === projectId;
}

export function compareDisciplines(a: Discipline, b: Discipline): number {
  return a.position - b.position || compareNames(a.name, b.name);
}

/**
 * Disciplinas globais semeadas no bootstrap.
 *
 * Fundação e Estrutural saíram na Fase 23: viraram as peças da planilha de etapas
 * (ESTACAS, BLOCO, PILARES, LAJE…), que o catálogo semeia (`STAGE_REGISTRY`). A posição de
 * cada uma sai da ordem do catálogo, e não desta lista (`defaultDisciplinePosition`).
 */
/**
 * O catálogo semeado, já **medido**.
 *
 * A unidade vem por código do catálogo de unidades (`m2`, `m3`, `un`), e não por id:
 * o id só existe depois que as unidades são semeadas, e amarrar os dois pela ordem de
 * criação tornaria o bootstrap frágil.
 *
 * Nascer sem unidade era o que tornava a medição inalcançável: a obra que usa as
 * disciplinas do sistema — que é toda obra recém-criada — não tinha por onde começar a
 * medir sem antes cadastrar disciplina nova.
 */
export const DEFAULT_DISCIPLINES: readonly {
  name: string;
  color: string;
  unitCode: string;
}[] = [
  { name: 'Alvenaria', color: '#c2703d', unitCode: 'm2' },
  { name: 'Elétrica', color: '#d4a017', unitCode: 'un' },
  { name: 'Hidráulica', color: '#3b82c4', unitCode: 'un' },
];
